import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import DatePicker, { registerLocale } from 'react-datepicker'
import moment from 'moment'
import { el, tr } from 'date-fns/locale'
import ContentSection from '../../components/UI/ContentSection'
import CustomDatePicker from '../../components/UI/CustomDatePicker'
import TableToolbar from '../../components/Table/TableToolbar'
import TransactionsList, {
  transactionCsvColumns,
} from '../../components/TransactionsList/TransactionsList'
import useTransactions from '../../services/payment/useTransactions'
import { downloadCsv } from '../../utils/toCsv'
import { filterByColumns } from '../../utils/tableSearch'
import {
  matchesPaymentStatus,
  paymentMethodKey,
} from '../../utils/tableFilters'
import { formatPaymentStatus } from '../../helpers/formatPaymentStatus'
import FilterSelect from '../../components/Table/FilterSelect'
import 'react-datepicker/dist/react-datepicker.css'

registerLocale('el', el)
registerLocale('tr', tr)

// payment.models.Payment.STATUS_CHOICES
const PAYMENT_STATUSES = ['succeeded', 'pending', 'failed', 'refunded']

const Transactions = () => {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const organizationId = searchParams.get('organization')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [method, setMethod] = useState('')
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ])
  const [startDate, endDate] = dateRange
  const { data: transactions, isLoading } = useTransactions()

  useEffect(() => {
    document.title = `${t('transactions')} - ReportAll`
  }, [t])

  // Payment.STATUS_CHOICES in full, not just the states the current data
  // happens to contain: asking for failed payments and being told there are
  // none is an answer, and the option would otherwise vanish and reappear as
  // the data changes.
  const statusOptions = useMemo(
    () =>
      PAYMENT_STATUSES.map((value) => ({
        value,
        label: formatPaymentStatus(t, value).label,
      })),
    [t],
  )

  // `method` is free text on the backend ('card', 'Bank Transfer', and per
  // the model a 'Visa **** 4242'), so there is no choice list to offer. These
  // are the buckets the Method column draws, kept to the ones present.
  const methodOptions = useMemo(() => {
    const present = new Set(
      (transactions ?? []).map((transaction) =>
        paymentMethodKey(transaction.method),
      ),
    )
    return [
      { value: 'card', label: t('card') },
      { value: 'manual', label: t('manualPayment') },
    ].filter((option) => present.has(option.value as 'card' | 'manual'))
  }, [transactions, t])

  const csvColumns = useMemo(() => transactionCsvColumns(t), [t])

  const rows = useMemo(() => {
    let result = transactions ?? []

    if (status) {
      result = result.filter((transaction) =>
        matchesPaymentStatus(transaction.status, status),
      )
    }
    if (method) {
      result = result.filter(
        (transaction) => paymentMethodKey(transaction.method) === method,
      )
    }
    if (organizationId) {
      result = result.filter(
        (transaction) => String(transaction.organization_id) === organizationId,
      )
    }
    if (startDate) {
      result = result.filter(
        (transaction) =>
          !!transaction.charge_date &&
          moment(transaction.charge_date).isSameOrAfter(
            moment(startDate),
            'day',
          ),
      )
    }
    if (endDate) {
      result = result.filter(
        (transaction) =>
          !!transaction.charge_date &&
          moment(transaction.charge_date).isSameOrBefore(
            moment(endDate),
            'day',
          ),
      )
    }

    return filterByColumns(csvColumns, result, search)
  }, [
    transactions,
    organizationId,
    startDate,
    endDate,
    search,
    status,
    method,
    csvColumns,
  ])

  const handleExport = () => {
    downloadCsv(
      `transactions-${moment().format('YYYY-MM-DD')}.csv`,
      csvColumns,
      rows,
    )
  }

  return (
    <ContentSection>
      <TableToolbar
        title={t('transactions')}
        count={rows.length}
        search={search}
        onSearchChange={setSearch}
        filters={
          <>
            <FilterSelect
              allLabel={t('allStatuses')}
              value={status}
              options={statusOptions}
              onChange={setStatus}
            />
            {methodOptions.length > 0 && (
              <FilterSelect
                allLabel={t('allMethods')}
                value={method}
                options={methodOptions}
                onChange={setMethod}
              />
            )}
          </>
        }
        onExport={handleExport}
        isExportDisabled={rows.length === 0}
        leading={
          <DatePicker
            locale={
              i18n.language === 'el'
                ? 'el'
                : i18n.language === 'tr'
                  ? 'tr'
                  : 'en'
            }
            dateFormat='dd MMM yy'
            calendarClassName='bg-white'
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) =>
              setDateRange(update as [Date | null, Date | null])
            }
            customInput={
              <CustomDatePicker
                variant='pill'
                emptyLabel={t('selectDateRange')}
              />
            }
          />
        }
      />
      <TransactionsList isLoading={isLoading} transactions={rows} />
    </ContentSection>
  )
}

export default Transactions
